// SPDX-License-Identifier: AGPL-3.0-or-later

import {showGenericErrorModal} from "@app/features/app/components/alerts/GenericErrorModalCommands";
import * as Modal from "@app/features/app/components/dialogs/Modal";
import {AVATAR_RECOMMENDED_SIZE_LABEL, STATIC_IMAGE_FORMATS} from "@app/features/app/config/I18nDisplayConstants";
import {GlobalLimits} from "@app/features/app/utils/GlobalLimits";
import {AssetCropModal, AssetType} from "@app/features/expressions/components/modals/AssetCropModal";
import {openAssetSourceModal} from "@app/features/expressions/components/modals/AssetSourceModal";
import {isAnimatedFile} from "@app/features/expressions/utils/AnimatedImageUtils";
import {formatImageUploadRecommendedHint} from "@app/features/expressions/utils/AssetUploadHintCopy";
import {isSvgFile, readImageFileAsUploadDataUrl} from "@app/features/expressions/utils/ImageUploadFileUtils";
import * as GuildCommands from "@app/features/guild/commands/GuildCommands";
import * as AvatarUtils from "@app/features/user/utils/AvatarUtils";
import {GuildAcronym} from "@app/features/guild/components/GuildAcronym";
import {
	ANIMATED_ICONS_ARE_NOT_SUPPORTED_WHEN_CREATING_A_DESCRIPTOR,
	ICON_FILE_IS_TOO_LARGE_PLEASE_CHOOSE_A_DESCRIPTOR,
} from "@app/features/guild/components/modals/add_guild_modal/shared";
import styles from "@app/features/guild/components/modals/TemplateAcceptModal.module.css";
import {
	CLOSE_DESCRIPTOR,
	CREATE_DESCRIPTOR,
	FAILED_TO_PROCESS_CROPPED_IMAGE_DESCRIPTOR,
	INVALID_IMAGE_TRY_ANOTHER_DESCRIPTOR,
} from "@app/features/i18n/utils/CommonMessageDescriptors";
import {openFilePicker} from "@app/features/messaging/utils/FilePickerUtils";
import {formatFileSize} from "@app/features/messaging/utils/FileUtils";
import Navigation from "@app/features/navigation/state/Navigation";
import {readBlobAsDataUrl} from "@app/features/platform/utils/BlobDataUrl";
import * as ColorUtils from "@app/features/theme/utils/ColorUtils";
import {Button} from "@app/features/ui/button/Button";
import * as ModalCommands from "@app/features/ui/commands/ModalCommands";
import {modal} from "@app/features/ui/commands/ModalCommands";
import {Input} from "@app/features/ui/components/form/FormInput";
import {AnnouncementChannelIcon} from "@app/features/ui/components/icons/AnnouncementChannelIcon";
import {TextChannelIcon} from "@app/features/ui/components/icons/TextChannelIcon";
import {VoiceChannelIcon} from "@app/features/ui/components/icons/VoiceChannelIcon";
import {Spinner} from "@app/features/ui/components/Spinner";
import {extractErrorMessage} from "@app/lib/forms";
import {getAcceptString} from "@fluxer/constants/src/AssetFormatPolicy";
import {ChannelTypes} from "@fluxer/constants/src/ChannelConstants";
import type {GuildTemplateResponse, TemplateChannel} from "@fluxer/schema/src/domains/guild/GuildTemplateSchemas";
import {msg} from "@lingui/core/macro";
import {Plural, Trans, useLingui} from "@lingui/react/macro";
import {CameraIcon, CaretDownIcon, CopyIcon, XIcon} from "@phosphor-icons/react";
import {clsx} from "clsx";
import {observer} from "mobx-react-lite";
import type React from "react";
import {useCallback, useEffect, useId, useMemo, useState} from "react";

const MODAL_TITLE_DESCRIPTOR = msg({
	message: "Create Server from Template",
	comment: "Title of the modal to create a server from a template.",
});

const MODAL_DESCRIPTION_DESCRIPTOR = msg({
	message: "Your new server will have the channels, roles, and settings from this template.",
	comment: "Description of the modal to create a server from a template.",
});

const SERVER_NAME_DESCRIPTOR = msg({
	message: "Server Name",
	comment: "Label for the new server name input field.",
});

const TEMPLATE_NOT_FOUND_TITLE_DESCRIPTOR = msg({
	message: "Template Not Found",
	comment: "Title shown when a template does not exist or has expired.",
});

const TEMPLATE_NOT_FOUND_DESC_DESCRIPTOR = msg({
	message: "This server template is invalid, deleted, or has expired.",
	comment: "Explanation shown when a template does not exist or has expired.",
});

const CHANGE_ICON_DESCRIPTOR = msg({
	message: "Change icon",
	comment: "Title of the modal where the user picks a create-community icon source.",
});

const IMAGE_COULDN_T_BE_USED_DESCRIPTOR = msg({
	message: "Image couldn't be used",
	comment: "Error modal title shown when a create-community icon upload cannot be accepted or processed.",
});

interface TemplateAcceptModalProps {
	code: string;
}

export const TemplateAcceptModal: React.FC<TemplateAcceptModalProps> = observer(({code}) => {
	const {i18n} = useLingui();
	const nameInputId = useId();

	const [template, setTemplate] = useState<GuildTemplateResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [notFound, setNotFound] = useState(false);

	const [serverName, setServerName] = useState("");
	const [serverIcon, setServerIcon] = useState<string | null>(null);
	const [iconCleared, setIconCleared] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const templateIconUrl = useMemo(() => {
		const iconHash = template?.serialized_source_guild?.icon_hash;
		const sourceGuildId = template?.source_guild_id ?? template?.serialized_source_guild?.source_guild_id;
		if (!iconHash || !sourceGuildId) return null;
		return AvatarUtils.getGuildIconURL({id: String(sourceGuildId), icon: iconHash});
	}, [template]);

	const activeIconUrl = serverIcon ?? (!iconCleared ? templateIconUrl : null);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setNotFound(false);

		GuildCommands.fetchTemplateByCode(code)
			.then((res) => {
				if (active) {
					setTemplate(res);
					setServerName(res.name || "");
					setLoading(false);
				}
			})
			.catch(() => {
				if (active) {
					setNotFound(true);
					setLoading(false);
				}
			});

		return () => {
			active = false;
		};
	}, [code]);

	const handleClose = () => {
		ModalCommands.pop();
	};

	const showIconUploadErrorModal = useCallback(
		(message: string) => {
			showGenericErrorModal({
				title: i18n._(IMAGE_COULDN_T_BE_USED_DESCRIPTOR),
				message,
				dataFlx: "guild.template-accept-modal.icon-upload-error-modal",
				defer: true,
			});
		},
		[i18n],
	);

	const handleIconUpload = useCallback(async () => {
		try {
			const [file] = await openFilePicker({accept: getAcceptString("guild_icon", {animatedAllowed: false})});
			if (!file) return;
			if (file.size > GlobalLimits.getAvatarMaxSize()) {
				showIconUploadErrorModal(
					i18n._(ICON_FILE_IS_TOO_LARGE_PLEASE_CHOOSE_A_DESCRIPTOR, {
						imageMaxSizeLabel: formatFileSize(i18n.locale, GlobalLimits.getAvatarMaxSize()),
					}),
				);
				return;
			}
			const svg = isSvgFile(file);
			const animated = svg ? false : await isAnimatedFile(file);
			if (animated) {
				showIconUploadErrorModal(i18n._(ANIMATED_ICONS_ARE_NOT_SUPPORTED_WHEN_CREATING_A_DESCRIPTOR));
				return;
			}
			const base64 = svg ? await readImageFileAsUploadDataUrl(file) : await readBlobAsDataUrl(file);
			ModalCommands.push(
				modal(() => (
					<AssetCropModal
						assetType={AssetType.GUILD_ICON}
						imageUrl={base64}
						onCropComplete={(croppedBlob) => {
							void readBlobAsDataUrl(croppedBlob).then(
								(croppedBase64) => {
									setServerIcon(croppedBase64);
									setIconCleared(false);
								},
								() => {
									showIconUploadErrorModal(i18n._(FAILED_TO_PROCESS_CROPPED_IMAGE_DESCRIPTOR));
								},
							);
						}}
						onSkip={() => {
							setServerIcon(base64);
							setIconCleared(false);
						}}
						data-flx="guild.template-accept-modal.asset-crop-modal"
					/>
				)),
			);
		} catch {
			showIconUploadErrorModal(i18n._(INVALID_IMAGE_TRY_ANOTHER_DESCRIPTOR));
		}
	}, [i18n, showIconUploadErrorModal]);

	const handleOpenIconPicker = useCallback(() => {
		openAssetSourceModal({
			title: i18n._(CHANGE_ICON_DESCRIPTOR),
			uploadHint: formatImageUploadRecommendedHint(i18n, {
				formats: STATIC_IMAGE_FORMATS,
				maxSize: formatFileSize(i18n.locale, GlobalLimits.getAvatarMaxSize()),
				recommendedSize: AVATAR_RECOMMENDED_SIZE_LABEL,
			}),
			onPickUpload: handleIconUpload,
			showGifOption: false,
		});
	}, [handleIconUpload, i18n]);

	const handleRemoveIcon = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		setServerIcon(null);
		setIconCleared(true);
	}, []);

	const handleCreate = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!template || !serverName.trim()) return;

		try {
			setSubmitting(true);
			setError(null);
			const templateData = {
				...template.serialized_source_guild,
				source_guild_id: template.source_guild_id ?? template.serialized_source_guild.source_guild_id,
			};
			if (iconCleared) {
				templateData.icon_hash = null;
				templateData.banner_hash = null;
			}
			const created = await GuildCommands.createFromTemplate({
				name: serverName.trim(),
				icon: serverIcon,
				template: templateData,
			});
			handleClose();
			Navigation.navigateToGuild(created.id);
		} catch (err) {
			setError(extractErrorMessage(i18n, err));
		} finally {
			setSubmitting(false);
		}
	};

	const sourceGuild = template?.serialized_source_guild;
	const channelCount = sourceGuild?.channels ? sourceGuild.channels.length : 0;
	const roleCount = sourceGuild?.roles ? sourceGuild.roles.length : 0;
	const botCount = sourceGuild?.bots?.length ?? 0;
	const pinnedCount = useMemo(() => {
		const pins = sourceGuild?.pinned_messages;
		if (!pins) return 0;
		return Object.values(pins).reduce((sum, list) => sum + (list?.length ?? 0), 0);
	}, [sourceGuild]);
	const messageCount = useMemo(() => {
		const msgs = sourceGuild?.channel_messages;
		if (!msgs) return 0;
		return Object.values(msgs).reduce((sum, list) => sum + (list?.length ?? 0), 0);
	}, [sourceGuild]);

	// Organize channels into categories and standalone channels
	const organizedChannels = useMemo(() => {
		if (!sourceGuild?.channels) return {categories: [], standalone: []};
		const channels = [...sourceGuild.channels].sort((a, b) => a.position - b.position);
		const categories: Array<{category: TemplateChannel; children: Array<TemplateChannel>}> = [];
		const standalone: Array<TemplateChannel> = [];

		const catMap = new Map<string, Array<TemplateChannel>>();
		const catObjs = new Map<string, TemplateChannel>();

		for (const ch of channels) {
			if (ch.type === ChannelTypes.GUILD_CATEGORY) {
				catObjs.set(String(ch.id), ch);
				if (!catMap.has(String(ch.id))) {
					catMap.set(String(ch.id), []);
				}
			}
		}

		for (const ch of channels) {
			if (ch.type === ChannelTypes.GUILD_CATEGORY) continue;
			if (ch.parent_id && catMap.has(String(ch.parent_id))) {
				catMap.get(String(ch.parent_id))!.push(ch);
			} else {
				standalone.push(ch);
			}
		}

		for (const [catId, cat] of catObjs.entries()) {
			categories.push({
				category: cat,
				children: catMap.get(catId) ?? [],
			});
		}

		return {categories, standalone};
	}, [sourceGuild?.channels]);

	// Sorted roles (excluding @everyone or displaying clean)
	const sortedRoles = useMemo(() => {
		if (!sourceGuild?.roles) return [];
		return [...sourceGuild.roles];
	}, [sourceGuild?.roles]);

	const renderChannelIcon = (type: number) => {
		if (type === ChannelTypes.GUILD_VOICE) {
			return <VoiceChannelIcon size={16} className={styles.channelIcon} data-flx="guild.template-accept-modal.voice-channel-icon" />;
		}
		if (type === ChannelTypes.GUILD_ANNOUNCEMENT) {
			return <AnnouncementChannelIcon size={16} className={styles.channelIcon} data-flx="guild.template-accept-modal.announcement-channel-icon" />;
		}
		return <TextChannelIcon size={16} className={styles.channelIcon} data-flx="guild.template-accept-modal.text-channel-icon" />;
	};

	if (loading) {
		return (
			<Modal.Root centered size="medium" onClose={handleClose} data-flx="guild.template-accept-modal.root.loading">
				<Modal.Header title={i18n._(MODAL_TITLE_DESCRIPTOR)} data-flx="guild.template-accept-modal.header.loading" />
				<Modal.Content data-flx="guild.template-accept-modal.content.loading">
					<div className={styles.loadingContainer} data-flx="guild.template-accept-modal.loading-container">
						<Spinner data-flx="guild.template-accept-modal.spinner" />
					</div>
				</Modal.Content>
			</Modal.Root>
		);
	}

	if (notFound || !template) {
		return (
			<Modal.Root centered size="medium" onClose={handleClose} data-flx="guild.template-accept-modal.root.not-found">
				<Modal.Header
					title={i18n._(TEMPLATE_NOT_FOUND_TITLE_DESCRIPTOR)}
					data-flx="guild.template-accept-modal.header.not-found"
				>
					<Modal.Description data-flx="guild.template-accept-modal.description.not-found">
						{i18n._(TEMPLATE_NOT_FOUND_DESC_DESCRIPTOR)}
					</Modal.Description>
				</Modal.Header>
				<Modal.Footer data-flx="guild.template-accept-modal.footer.not-found">
					<Button variant="primary" onClick={handleClose} data-flx="guild.template-accept-modal.close-btn">
						{i18n._(CLOSE_DESCRIPTOR)}
					</Button>
				</Modal.Footer>
			</Modal.Root>
		);
	}

	return (
		<Modal.Root centered size="medium" onClose={handleClose} data-flx="guild.template-accept-modal.root">
			<form onSubmit={handleCreate} style={{display: "contents"}} data-flx="guild.template-accept-modal.form">
				<Modal.Header
					title={i18n._(MODAL_TITLE_DESCRIPTOR)}
					data-flx="guild.template-accept-modal.header"
				>
					<Modal.Description data-flx="guild.template-accept-modal.description">
						{i18n._(MODAL_DESCRIPTION_DESCRIPTOR)}
					</Modal.Description>
				</Modal.Header>

				<Modal.Content data-flx="guild.template-accept-modal.content">
					<div className={styles.container} data-flx="guild.template-accept-modal.container">
						<div className={styles.templatePreviewCard} data-flx="guild.template-accept-modal.preview-card">
							<div className={styles.iconCircle} data-flx="guild.template-accept-modal.icon-circle">
								{templateIconUrl ? (
									<img
										src={templateIconUrl}
										alt=""
										className={styles.previewCardIconImage}
										data-flx="guild.template-accept-modal.preview-card-icon-image"
									/>
								) : (
									<CopyIcon size={24} weight="bold" className={styles.iconOnBrand} data-flx="guild.template-accept-modal.icon-on-brand" />
								)}
							</div>
							<div className={styles.previewInfo} data-flx="guild.template-accept-modal.preview-info">
								<span className={styles.templateName} data-flx="guild.template-accept-modal.template-name">
									{template.name}
								</span>
								{template.description && (
									<span className={styles.templateDescription} data-flx="guild.template-accept-modal.template-desc">
										{template.description}
									</span>
								)}
								<div className={styles.statsRow} data-flx="guild.template-accept-modal.stats-row">
									<span data-flx="guild.template-accept-modal.span">
										<Plural value={channelCount} one="# channel" other="# channels" data-flx="guild.template-accept-modal.plural" />
									</span>
									<span className={styles.statDot} data-flx="guild.template-accept-modal.stat-dot" />
									<span data-flx="guild.template-accept-modal.span--2">
										<Plural value={roleCount} one="# role" other="# roles" data-flx="guild.template-accept-modal.plural--2" />
									</span>
									{botCount > 0 && (
										<>
											<span className={styles.statDot} data-flx="guild.template-accept-modal.stat-dot--bots" />
											<span data-flx="guild.template-accept-modal.span--bots">
												<Plural value={botCount} one="# bot" other="# bots" data-flx="guild.template-accept-modal.plural--bots" />
											</span>
										</>
									)}
									{pinnedCount > 0 && (
										<>
											<span className={styles.statDot} data-flx="guild.template-accept-modal.stat-dot--pins" />
											<span data-flx="guild.template-accept-modal.span--pins">
												<Plural value={pinnedCount} one="# pinned message" other="# pinned messages" data-flx="guild.template-accept-modal.plural--pins" />
											</span>
										</>
									)}
									{messageCount > 0 && (
										<>
											<span className={styles.statDot} data-flx="guild.template-accept-modal.stat-dot--msgs" />
											<span data-flx="guild.template-accept-modal.span--msgs">
												<Plural value={messageCount} one="# message" other="# messages" data-flx="guild.template-accept-modal.plural--msgs" />
											</span>
										</>
									)}
								</div>
							</div>
						</div>

						{error && (
							<p className={styles.errorText} data-flx="guild.template-accept-modal.error-text">
								{error}
							</p>
						)}

						{/* Icon uploader */}
						<div className={styles.iconUploadRow} data-flx="guild.template-accept-modal.icon-upload-row">
							<div className={styles.iconUploadWrapper} data-flx="guild.template-accept-modal.icon-upload-wrapper">
								<button
									type="button"
									onClick={handleOpenIconPicker}
									className={clsx(styles.iconUploadButton, activeIconUrl && styles.iconUploadButtonWithPreview)}
									style={activeIconUrl ? {backgroundImage: `url(${activeIconUrl})`} : undefined}
									data-flx="guild.template-accept-modal.icon-upload-button"
								>
									{!activeIconUrl && (
										serverName.trim() ? (
											<GuildAcronym name={serverName} className={styles.iconAcronym} data-flx="guild.template-accept-modal.guild-acronym" />
										) : (
											<div className={styles.iconUploadBadge} data-flx="guild.template-accept-modal.upload-badge">
												<CameraIcon size={24} weight="bold" data-flx="guild.template-accept-modal.camera-icon" />
											</div>
										)
									)}
								</button>
								{activeIconUrl && (
									<button
										type="button"
										onClick={handleRemoveIcon}
										className={styles.removeIconButton}
										title={i18n._(CLOSE_DESCRIPTOR)}
										data-flx="guild.template-accept-modal.remove-icon-btn"
									>
										<XIcon size={12} weight="bold" data-flx="guild.template-accept-modal.x-icon" />
									</button>
								)}
							</div>
							<div className={styles.iconUploadMeta} data-flx="guild.template-accept-modal.icon-upload-meta">
								<span className={styles.iconUploadTitle} data-flx="guild.template-accept-modal.icon-upload-title">
									<Trans>Server icon</Trans>
								</span>
								<span className={styles.iconUploadHint} data-flx="guild.template-accept-modal.icon-upload-hint">
									<Trans>Upload a server icon or keep the default.</Trans>
								</span>
							</div>
						</div>

						<div className={styles.formGroup} data-flx="guild.template-accept-modal.form-group">
							<label htmlFor={nameInputId} className={styles.label} data-flx="guild.template-accept-modal.label">
								{i18n._(SERVER_NAME_DESCRIPTOR)}
							</label>
							<Input
								id={nameInputId}
								value={serverName}
								onChange={(e) => setServerName(e.target.value)}
								disabled={submitting}
								required
								autoFocus
								data-flx="guild.template-accept-modal.server-name-input"
							/>
						</div>

						{/* Template Channels Section */}
						{channelCount > 0 && (
							<div className={styles.section} data-flx="guild.template-accept-modal.channels-section">
								<div className={styles.sectionHeader} data-flx="guild.template-accept-modal.channels-header">
									<Trans>Template Channels</Trans>
								</div>
								<div className={styles.previewBox} data-flx="guild.template-accept-modal.channels-box">
									{organizedChannels.categories.map(({category, children}) => (
										<div key={category.id} className={styles.categoryGroup} data-flx="guild.template-accept-modal.category-group">
											<div className={styles.categoryHeader} data-flx="guild.template-accept-modal.category-header">
												<CaretDownIcon size={12} weight="bold" className={styles.categoryArrow} data-flx="guild.template-accept-modal.caret-icon" />
												<span data-flx="guild.template-accept-modal.category-name">{category.name}</span>
											</div>
											<div className={styles.categoryChildren} data-flx="guild.template-accept-modal.category-children">
												{children.map((ch) => (
													<div key={ch.id} className={styles.channelItem} data-flx="guild.template-accept-modal.channel-item">
														{renderChannelIcon(ch.type)}
														<span className={styles.channelName} data-flx="guild.template-accept-modal.channel-name">{ch.name}</span>
													</div>
												))}
											</div>
										</div>
									))}
									{organizedChannels.standalone.map((ch) => (
										<div key={ch.id} className={styles.channelItem} data-flx="guild.template-accept-modal.channel-item--standalone">
											{renderChannelIcon(ch.type)}
											<span className={styles.channelName} data-flx="guild.template-accept-modal.channel-name--standalone">{ch.name}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Template Roles Section */}
						{roleCount > 0 && (
							<div className={styles.section} data-flx="guild.template-accept-modal.roles-section">
								<div className={styles.sectionHeader} data-flx="guild.template-accept-modal.roles-header">
									<Trans>Template Roles and Permissions</Trans>
								</div>
								<div className={styles.rolesBox} data-flx="guild.template-accept-modal.roles-box">
									{sortedRoles.map((role) => {
										const roleColor = role.color && role.color !== 0 ? ColorUtils.int2rgb(role.color) : "var(--text-tertiary)";
										return (
											<div key={role.id} className={styles.roleBadge} data-flx="guild.template-accept-modal.role-badge">
												<span
													className={styles.roleDot}
													style={{backgroundColor: roleColor}}
													data-flx="guild.template-accept-modal.role-dot"
												/>
												<span className={styles.roleName} data-flx="guild.template-accept-modal.role-name">
													{role.name || "@everyone"}
												</span>
											</div>
										);
									})}
								</div>
							</div>
						)}

						<p className={styles.helpNotice} data-flx="guild.template-accept-modal.help-notice">
							<Trans>Channels, roles, and permissions can be customized anytime after creation.</Trans>
						</p>
					</div>
				</Modal.Content>

				<Modal.Footer data-flx="guild.template-accept-modal.footer">
					<Button
						variant="secondary"
						type="button"
						onClick={handleClose}
						disabled={submitting}
						data-flx="guild.template-accept-modal.close-btn"
					>
						{i18n._(CLOSE_DESCRIPTOR)}
					</Button>
					<Button
						variant="primary"
						type="submit"
						disabled={submitting || !serverName.trim()}
						submitting={submitting}
						data-flx="guild.template-accept-modal.create-btn"
					>
						{i18n._(CREATE_DESCRIPTOR)}
					</Button>
				</Modal.Footer>
			</form>
		</Modal.Root>
	);
});

export default TemplateAcceptModal;
